import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';

import { useIsCardPullFrozen } from '#src/hooks/my-cards/useIsCardPullFrozen.ts';

import type { CraftFreezeOverlay_currentUser } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--double-unit);
  right: var(--double-unit);
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      to bottom,
      var(--c-nl-800),
      transparent 200px,
      transparent calc(100% - 300px),
      var(--c-nl-800)
    );
  }
`;

const ContentWrapper = styled(Vertical)`
  background: var(--c-nd-100-opaque);
  padding: var(--quadruple-unit);
  border-radius: var(--unit);
  margin: auto;
  max-width: 600px;
`;

export const CraftFreezeOverlay = ({
  currentUser,
}: {
  currentUser: Nullable<CraftFreezeOverlay_currentUser>;
}) => {
  const cardPullFrozen = useIsCardPullFrozen(currentUser);

  if (!cardPullFrozen) {
    return null;
  }
  return (
    <Wrapper>
      <ContentWrapper center gap={4} className="text-center">
        <BrandM>
          <FormattedMessage defaultMessage="Crafting temporarily closed" />
        </BrandM>
        <Vertical>
          <BodyM>
            <FormattedMessage defaultMessage="Crafting is temporarily closed, coming back soon!" />
          </BodyM>
        </Vertical>
      </ContentWrapper>
    </Wrapper>
  );
};

CraftFreezeOverlay.fragments = {
  currentUser: gql`
    fragment CraftFreezeOverlay_currentUser on CurrentUser {
      slug
      ...useIsCardPullFrozen_currentUser
    }
    ${useIsCardPullFrozen.fragments.currentUser}
  ` as TypedDocumentNode<CraftFreezeOverlay_currentUser>,
};
